import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowRight,
  Briefcase,
  Calendar,
  CheckCircle2,
  Clock,
  Eye,
  FileText,
  MapPin,
  RefreshCcw,
  Search,
  Send,
  ShieldCheck,
  XCircle,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  manproQueryKeys,
  mapProviderBidToRow,
  providerBidService,
  type BidStatus,
  type PaginatedResponse,
  type ProviderBidDto,
} from '../../api/manpro';

type SummaryCard = {
  label: string;
  value: number;
  icon: LucideIcon;
  tone: string;
};

const statusTone: Record<BidStatus, string> = {
  pending: 'bg-blue-50 text-blue-700',
  submitted: 'bg-blue-50 text-blue-700',
  accepted: 'bg-emerald-50 text-emerald-700',
  denied: 'bg-red-50 text-red-700',
  rejected: 'bg-red-50 text-red-700',
  withdrawn: 'bg-slate-100 text-slate-600',
};

const statusIcon: Record<BidStatus, typeof Clock> = {
  pending: Clock,
  submitted: Clock,
  accepted: CheckCircle2,
  denied: XCircle,
  rejected: XCircle,
  withdrawn: RefreshCcw,
};

const bidDocumentCategoryLabels: Record<string, string> = {
  portfolio: 'Portfolio',
  company_profile: 'Company Profile',
  rab: 'RAB / Breakdown',
  method: 'Metode Kerja',
  legal: 'Legalitas',
  other: 'Lainnya',
};

const isPaginatedResponse = (
  payload?: PaginatedResponse<ProviderBidDto> | ProviderBidDto[] | null,
): payload is PaginatedResponse<ProviderBidDto> =>
  Boolean(payload && !Array.isArray(payload) && Array.isArray(payload.data));

const getBidRows = (payload?: PaginatedResponse<ProviderBidDto> | ProviderBidDto[] | null) => {
  if (Array.isArray(payload)) return payload;
  return payload?.data ?? [];
};

const getErrorMessage = (error: unknown) => {
  const maybeError = error as {
    response?: {
      data?: {
        message?: string;
        errors?: Array<{ message?: string }>;
      };
    };
  };

  return maybeError.response?.data?.errors?.[0]?.message
    || maybeError.response?.data?.message
    || 'Aksi penawaran gagal diproses.';
};

const ProviderBids = () => {
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [expandedBidId, setExpandedBidId] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const queryParams = useMemo(
    () => ({
      offset: page,
      limit: 10,
      status: statusFilter || undefined,
    }),
    [page, statusFilter],
  );

  const bidsQuery = useQuery({
    queryKey: manproQueryKeys.providerBids(queryParams),
    // Penawaran Saya harus membaca bid Provider dari database, bukan dari prototype Owner.
    queryFn: () => providerBidService.getProviderBids(queryParams),
    // Auto-refresh 30 detik: status bid bisa berubah oleh Owner (diterima/ditolak) kapan saja,
    // sehingga provider tidak perlu reload manual untuk melihat status terbaru.
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
  });

  const rawBids = getBidRows(bidsQuery.data);
  const bidRows = useMemo(() => rawBids.map(mapProviderBidToRow), [rawBids]);
  const pagination = isPaginatedResponse(bidsQuery.data) ? bidsQuery.data : null;

  const filteredRows = useMemo(
    () =>
      bidRows.filter((bid) =>
        `${bid.title} ${bid.category} ${bid.location} ${bid.note} ${bid.statusLabel}`
          .toLowerCase()
          .includes(query.toLowerCase()),
      ),
    [bidRows, query],
  );

  const withdrawMutation = useMutation({
    mutationFn: (bidId: string) => providerBidService.withdrawProviderBid(bidId),
    onSuccess: () => {
      toast.success('Bid berhasil ditarik.');
      // Withdraw mengubah data di backend, lalu list bid dan opportunity di-refresh supaya tombol Kirim Bid/Lihat Bid sinkron.
      queryClient.invalidateQueries({ queryKey: ['manpro', 'provider', 'bids'] });
      queryClient.invalidateQueries({ queryKey: ['manpro', 'provider', 'opportunities'] });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });

  const acceptedCount = bidRows.filter((row) => row.status === 'accepted').length;
  const submittedCount = bidRows.filter((row) => row.status === 'submitted' || row.status === 'pending').length;
  const rejectedCount = bidRows.filter((row) => row.status === 'rejected' || row.status === 'denied').length;
  const withdrawnCount = bidRows.filter((row) => row.status === 'withdrawn').length;
  const summaryCards: SummaryCard[] = [
    { label: 'Menunggu Review', value: submittedCount, icon: Clock, tone: 'bg-blue-50 text-blue-700' },
    { label: 'Diterima', value: acceptedCount, icon: CheckCircle2, tone: 'bg-emerald-50 text-emerald-700' },
    { label: 'Tidak Terpilih', value: rejectedCount, icon: XCircle, tone: 'bg-red-50 text-red-700' },
    { label: 'Ditarik', value: withdrawnCount, icon: RefreshCcw, tone: 'bg-slate-100 text-slate-600' },
  ];
  const currentPage = pagination?.current_page ?? page;
  const lastPage = pagination?.last_page ?? 1;
  const totalRows = pagination?.total ?? bidRows.length;

  return (
    <div className="space-y-6">
      <section className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">Penawaran Saya</h1>
          <p className="mt-2 text-sm text-slate-500">
            Pantau semua bid yang sudah dikirim ke Owner dan lanjutkan proyek yang dimenangkan ke halaman kelola proyek.
          </p>
        </div>
        <Link
          to="/provider-opportunities"
          className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-5 py-3 text-sm font-extrabold text-white shadow-sm transition hover:bg-blue-700"
        >
          <Send size={17} />
          Cari Peluang Baru
        </Link>
      </section>

      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {summaryCards.map(({ label, value, icon: Icon, tone }) => (
          <div key={label} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-extrabold text-slate-800">{label}</p>
                <p className="mt-3 text-3xl font-extrabold text-slate-900">{value}</p>
              </div>
              <div className={`grid h-12 w-12 place-items-center rounded-full ${tone}`}>
                <Icon size={22} />
              </div>
            </div>
          </div>
        ))}
      </section>

      <section className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_310px]">
        <div className="min-w-0 space-y-4">
          <div className="grid gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm md:grid-cols-[minmax(0,1fr)_220px]">
            <label className="flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2.5 text-sm text-slate-500">
              <Search size={17} />
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                className="w-full bg-transparent outline-none"
                placeholder="Cari proyek, kategori, lokasi..."
              />
            </label>
            <select
              value={statusFilter}
              onChange={(event) => {
                setStatusFilter(event.target.value);
                setPage(1);
              }}
              className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-semibold text-slate-600 outline-none focus:border-blue-400"
            >
              <option value="">Semua Status</option>
              <option value="submitted">Menunggu Review</option>
              <option value="accepted">Diterima</option>
              <option value="rejected">Ditolak</option>
              <option value="withdrawn">Ditarik</option>
            </select>
          </div>

          {bidsQuery.isLoading && (
            <div className="space-y-4">
              {Array.from({ length: 4 }).map((_, index) => (
                <div key={index} className="h-56 animate-pulse rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" />
              ))}
            </div>
          )}

          {bidsQuery.isError && (
            <div className="rounded-2xl border border-rose-100 bg-rose-50 p-5 text-sm font-semibold text-rose-700">
              Gagal memuat penawaran Provider. Pastikan akun Anda memiliki akses Provider.
            </div>
          )}

          {!bidsQuery.isLoading && !bidsQuery.isError && (
            <p className="text-sm text-slate-500">
              Menampilkan {filteredRows.length} dari {totalRows} penawaran.
            </p>
          )}

          {!bidsQuery.isLoading &&
            !bidsQuery.isError &&
            filteredRows.map((bid) => {
              const StatusIcon = statusIcon[bid.status];
              const acceptedHasProject = Boolean(bid.manproProjectId);

              return (
                <article key={bid.id} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition hover:border-blue-100 hover:shadow-md">
                  <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_270px]">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-extrabold ${statusTone[bid.status]}`}>
                          <StatusIcon size={14} />
                          {bid.statusLabel}
                        </span>
                        {bid.isAccepted && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-3 py-1 text-xs font-extrabold text-emerald-700">
                            <ShieldCheck size={14} />
                            Provider Terpilih
                          </span>
                        )}
                      </div>

                      <h2 className="mt-3 text-xl font-extrabold text-slate-900">{bid.title}</h2>
                      <p className="mt-2 text-sm leading-6 text-slate-500">{bid.note}</p>
                      <div className="mt-4 grid gap-3 text-sm font-semibold text-slate-600 sm:grid-cols-3">
                        <span className="inline-flex items-center gap-2">
                          <MapPin size={16} className="text-slate-400" />
                          {bid.location}
                        </span>
                        <span className="inline-flex items-center gap-2">
                          <Briefcase size={16} className="text-slate-400" />
                          {bid.category}
                        </span>
                        <span className="inline-flex items-center gap-2">
                          <Calendar size={16} className="text-slate-400" />
                          {bid.duration}
                        </span>
                      </div>

                      {bid.documentLinks.length ? (
                        <div className="mt-4 rounded-2xl border border-slate-100 bg-slate-50 p-3">
                          <p className="text-xs font-extrabold text-slate-700">Dokumen yang dikirim</p>
                          <div className="mt-3 flex flex-wrap gap-2">
                            {bid.documentLinks.map((document) => (
                              <a
                                key={document.id}
                                href={document.resolved_url}
                                target="_blank"
                                rel="noreferrer"
                                className="rounded-full bg-white px-3 py-1 text-xs font-bold text-slate-600 transition hover:bg-blue-50 hover:text-blue-700"
                              >
                                {bidDocumentCategoryLabels[String(document.category || 'other')] || 'Dokumen'}: {document.name}
                              </a>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div className="mt-4 rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-3 text-xs font-semibold text-slate-400">
                          Bid ini belum memiliki dokumen pendukung.
                        </div>
                      )}

                      {expandedBidId === bid.id && (
                        <div className="mt-4 rounded-2xl border border-blue-100 bg-blue-50/60 p-4">
                          <div className="flex flex-wrap items-start justify-between gap-3">
                            <div>
                              <p className="text-sm font-extrabold text-slate-900">Detail Bid Provider</p>
                              <p className="mt-1 text-xs font-semibold text-slate-500">
                                Ringkasan penawaran yang dikirim ke Owner.
                              </p>
                            </div>
                            <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-extrabold ${statusTone[bid.status]}`}>
                              <StatusIcon size={14} />
                              {bid.statusLabel}
                            </span>
                          </div>

                          <div className="mt-4 grid gap-3 md:grid-cols-2">
                            {[
                              ['Nilai Bid', bid.amountLabel],
                              ['Durasi Estimasi', bid.duration],
                              ['Tanggal Submit', bid.submittedAtLabel],
                              ['Project', bid.title],
                              ['Kategori', bid.category],
                              ['Lokasi', bid.location],
                            ].map(([label, value]) => (
                              <div key={label} className="rounded-xl bg-white p-3">
                                <p className="text-xs font-extrabold text-slate-500">{label}</p>
                                <p className="mt-1 text-sm font-bold text-slate-800">{value || '-'}</p>
                              </div>
                            ))}
                          </div>

                          <div className="mt-4 grid gap-3 xl:grid-cols-2">
                            <div className="rounded-xl bg-white p-3">
                              <p className="text-xs font-extrabold text-slate-500">Catatan Penawaran</p>
                              <p className="mt-2 text-sm font-semibold leading-6 text-slate-700">{bid.note}</p>
                            </div>
                            <div className="rounded-xl bg-white p-3">
                              <p className="text-xs font-extrabold text-slate-500">Metode Kerja</p>
                              <p className="mt-2 text-sm font-semibold leading-6 text-slate-700">{bid.methodStatement}</p>
                            </div>
                            <div className="rounded-xl bg-white p-3 xl:col-span-2">
                              <p className="text-xs font-extrabold text-slate-500">Syarat Komersial</p>
                              <p className="mt-2 text-sm font-semibold leading-6 text-slate-700">{bid.commercialTerms}</p>
                            </div>
                          </div>

                          <div className="mt-4 rounded-xl bg-white p-3">
                            <p className="text-xs font-extrabold text-slate-500">Timeline Status</p>
                            <div className="mt-3 grid gap-2 text-xs font-semibold text-slate-600 sm:grid-cols-2">
                              <span>Submitted: {bid.submittedAtLabel}</span>
                              <span>Accepted: {bid.acceptedAt || '-'}</span>
                              <span>Rejected: {bid.rejectedAt || '-'}</span>
                              <span>Withdrawn: {bid.withdrawnAt || '-'}</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                      <p className="text-xs font-bold text-slate-500">Nilai Bid</p>
                      <p className="mt-1 text-xl font-extrabold text-slate-900">{bid.amountLabel}</p>
                      <p className="mt-3 text-xs font-semibold text-slate-500">
                        Dikirim: {bid.submittedAtLabel}
                      </p>

                      <button
                        type="button"
                        onClick={() => setExpandedBidId((current) => (current === bid.id ? null : bid.id))}
                        className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl border border-blue-200 bg-white px-4 py-2.5 text-sm font-extrabold text-blue-700 transition hover:bg-blue-50"
                      >
                        <Eye size={16} />
                        {expandedBidId === bid.id ? 'Tutup Detail Bid' : 'Lihat Detail Bid'}
                      </button>

                      {bid.isAccepted ? (
                        acceptedHasProject ? (
                          <Link
                            to={`/project-management?project_id=${encodeURIComponent(String(bid.manproProjectId))}`}
                            className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-extrabold text-white"
                          >
                            Kelola Project
                            <ArrowRight size={16} />
                          </Link>
                        ) : (
                          <button
                            type="button"
                            disabled
                            className="mt-4 inline-flex w-full cursor-not-allowed items-center justify-center rounded-xl bg-slate-200 px-4 py-2.5 text-sm font-extrabold text-slate-500"
                          >
                            Kelola proyek sedang disiapkan
                          </button>
                        )
                      ) : bid.canWithdraw ? (
                        <button
                          type="button"
                          onClick={() => withdrawMutation.mutate(bid.id)}
                          disabled={withdrawMutation.isPending}
                          className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl border border-rose-200 bg-white px-4 py-2.5 text-sm font-extrabold text-rose-600 transition hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          <RefreshCcw size={16} />
                          {withdrawMutation.isPending ? 'Menarik Bid...' : 'Tarik Bid'}
                        </button>
                      ) : (
                        <button
                          type="button"
                          disabled
                          className="mt-4 inline-flex w-full cursor-not-allowed items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-extrabold text-slate-500"
                        >
                          Arsip Penawaran
                        </button>
                      )}
                    </div>
                  </div>
                </article>
              );
            })}

          {!bidsQuery.isLoading && !bidsQuery.isError && filteredRows.length === 0 && (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center">
              <FileText size={38} className="mx-auto text-slate-300" />
              <p className="mt-3 font-bold text-slate-700">Belum ada penawaran dari akun ini</p>
              <p className="mt-1 text-sm text-slate-400">Kirim bid dari halaman Peluang Provider untuk mulai membangun pipeline.</p>
            </div>
          )}

          {lastPage > 1 && (
            <div className="flex flex-wrap items-center justify-center gap-2 pt-2">
              <button
                onClick={() => setPage((previous) => Math.max(previous - 1, 1))}
                disabled={currentPage <= 1}
                className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-600 disabled:cursor-not-allowed disabled:opacity-50"
                type="button"
              >
                Sebelumnya
              </button>
              <span className="rounded-xl bg-white px-4 py-2 text-sm font-extrabold text-slate-700">
                {currentPage} / {lastPage}
              </span>
              <button
                onClick={() => setPage((previous) => Math.min(previous + 1, lastPage))}
                disabled={currentPage >= lastPage}
                className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-600 disabled:cursor-not-allowed disabled:opacity-50"
                type="button"
              >
                Selanjutnya
              </button>
            </div>
          )}
        </div>

        <aside className="w-full space-y-4 lg:sticky lg:top-24 lg:w-[310px]">
          <div className="rounded-2xl border border-blue-100 bg-blue-50 p-5">
            <h2 className="font-extrabold text-slate-900">Flow Provider</h2>
            <div className="mt-4 space-y-3 text-sm font-semibold text-slate-600">
              {[
                'Cari peluang dari Owner',
                'Kirim nilai, durasi, dan catatan bid',
                'Pantau status review Owner',
                'Jika diterima, lanjut ke kelola proyek',
              ].map((item, index) => (
                <p key={item} className="flex gap-3">
                  <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-white text-xs font-extrabold text-blue-700">
                    {index + 1}
                  </span>
                  {item}
                </p>
              ))}
            </div>
          </div>
        </aside>
      </section>
    </div>
  );
};

export default ProviderBids;
